/**
 * Contexto de ejecución (Carlos 2026-08-09).
 *
 * "Jamás se ejecuta un análisis sin proyecto, ni environment, ni workspace."
 * El flujo es Proyecto de Planeación → Workspace → Environment → Sprint →
 * [Services, HTs], y ningún lanzamiento se acepta sin esa cadena completa.
 *
 * La PRESELECCIÓN la decide el servidor y aquí no se recalcula: con un solo
 * proyecto se autoselecciona y queda bloqueado; con varios el selector arranca
 * en el primero activo. Es una regla de producto, no una preferencia de
 * pantalla, y tres vistas no pueden interpretarla cada una a su manera.
 */
import { api } from "./client";

export interface ContextSprint {
  id: string;
  name: string;
  services: string[];
}

export interface ContextWorkspace {
  id: string;
  name: string;
  environment_id: number | null;
  environment_name: string | null;
  sprints: ContextSprint[];
  /** false = falta un eslabón para poder lanzar desde aquí. */
  utilizable: boolean;
  /** Por qué no se puede usar. Se muestra: ocultarlo lo haría parecer inexistente. */
  motivo: string | null;
}

export interface ContextPlanProject {
  id: string;
  name: string;
  code: string;
  status: string;
  workspaces: ContextWorkspace[];
}

/** Lo que ESTA sesión tiene fijado ahora mismo. */
export interface ContextoVigente {
  plan_project_id: string | null;
  workspace_id: string | null;
  project_id: string | null;
  service_id: string | null;
}

export interface ExecutionContextResponse {
  proyectos: ContextPlanProject[];
  /** Preselección resuelta en el servidor. */
  seleccionado: string | null;
  /** true con exactamente un proyecto: eje fijo, sin desplegable. */
  bloqueado: boolean;
  /** Con texto, la UI lo muestra en lugar del selector. */
  motivo_sin_contexto: string | null;
  /** Lo fijado por esta sesión. null = todavía nada, y entonces nada se ejecuta. */
  vigente: ContextoVigente | null;
  /**
   * La cadena EFECTIVA que el servidor considera vigente ahora mismo: lo fijado y,
   * donde no hay nada, las reglas del producto (proyecto único, primer workspace
   * utilizable, primer sprint).
   *
   * Esas reglas vivían aquí y no les corresponde: SAR-CORE tiene toda la
   * funcionalidad y esta app solo la consume (Carlos 2026-08-23).
   */
  efectivo: ContextoVigente | null;
}

export async function getExecutionContext(): Promise<ExecutionContextResponse> {
  const { data } = await api.get<ExecutionContextResponse>("/execution-context");
  return data;
}

/**
 * Fija los parámetros globales de ESTA sesión (Carlos 2026-08-22).
 *
 * Desde este cambio ningún endpoint de ejecución los recibe en el cuerpo: los lee de
 * la sesión. La pantalla es donde el humano elige; una vez elegido, se declara aquí y
 * después se ejecuta. Así el CLI, la API y la pantalla parten del mismo contexto en
 * vez de reconstruirlo cada una a su manera.
 *
 * El servidor valida la cadena ENTERA antes de aceptarla: no se puede saltar un
 * eslabón ni mezclar el workspace de un proyecto con el sprint de otro.
 */
export async function setExecutionContext(payload: {
  plan_project_id?: string | null;
  workspace_id?: string | null;
  project_id?: string | null;
  service_id?: string | null;
}): Promise<ExecutionContextResponse> {
  const { data } = await api.post<ExecutionContextResponse>("/execution-context/set", { ...payload });
  return data;
}

/** Deja la sesión sin contexto: la siguiente operación se detiene y lo pide. */
export async function clearExecutionContext(): Promise<ExecutionContextResponse> {
  const { data } = await api.post<ExecutionContextResponse>("/execution-context/clear", {  });
  return data;
}

// --- Administración de la cadena -------------------------------------------

export interface PlanWorkspaceLink {
  workspace_id: string;
  name: string;
  code: string;
  environment_id: number | null;
  environment_name: string | null;
  linked_at: string;
}

export interface PlanMember {
  user_id: number;
  username: string;
  role_in_project: string;
  assigned_at: string;
}

export async function listPlanWorkspaces(
  planProjectId: string,
): Promise<PlanWorkspaceLink[]> {
  const { data } = await api.post<PlanWorkspaceLink[]>("/plan/projects/workspaces/list", { plan_project_id: planProjectId });
  return data;
}

export async function linkPlanWorkspace(
  planProjectId: string,
  workspaceId: string,
): Promise<PlanWorkspaceLink> {
  const { data } = await api.post<PlanWorkspaceLink>("/plan/projects/workspaces", { workspace_id: workspaceId, plan_project_id: planProjectId });
  return data;
}

export async function unlinkPlanWorkspace(
  planProjectId: string,
  workspaceId: string,
): Promise<void> {
  await api.post("/plan/projects/workspaces/delete", { plan_project_id: planProjectId, workspace_id: workspaceId });
}

export async function listPlanMembers(planProjectId: string): Promise<PlanMember[]> {
  const { data } = await api.post<PlanMember[]>("/plan/projects/members/list", { plan_project_id: planProjectId });
  return data;
}

export async function addPlanMember(
  planProjectId: string,
  userId: number,
  roleInProject: string,
): Promise<PlanMember> {
  const { data } = await api.post<PlanMember>("/plan/projects/members", { user_id: userId, role_in_project: roleInProject, plan_project_id: planProjectId });
  return data;
}

export async function removePlanMember(
  planProjectId: string,
  userId: number,
): Promise<void> {
  await api.post("/plan/projects/members/delete", { plan_project_id: planProjectId, user_id: userId });
}
